import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { timer } from 'rxjs';
import { CommonModule } from '@angular/common';
import { UpgraderNewService } from './services/upgrader-new.service';
import { UpgraderHexagonComponent } from './components/upgrader-hexagon/upgrader-hexagon.component';
import { UpgraderInventoryComponent } from './components/upgrader-inventory/upgrader-inventory.component';
import { UpgraderTargetItemsComponent } from './components/upgrader-target-items/upgrader-target-items.component';
import { UpgraderOffsetSliderComponent } from './components/upgrader-offset-slider/upgrader-offset-slider.component';
import { UpgraderItemsInfoComponent } from './components/upgrader-items-info/upgrader-items-info.component';
import { UpgraderTargetPanelComponent } from './components/upgrader-target-panel/upgrader-target-panel.component';
import { SessionService } from '@core/services/session.service';
import { ClipboardService } from '@core/services/clipboard.service';
import { BalanceIconComponent } from '@core/components/balance-icon/balance-icon.component';
import { InputOptionsComponent } from '@core/components/input-options/input-options.component';
import { FairnessButtonComponent } from '@core/components/fairness-button/fairness-button.component';
import { fadeInAnimation } from '@core/utils/animations.util';
import { SvgIconComponent } from 'angular-svg-icon';
import { HEXAGON_RESET_DELAY_MS } from './upgrader.constants';

@Component({
  selector: 'app-upgrader-new',
  standalone: true,
  imports: [
    CommonModule,
    UpgraderHexagonComponent,
    UpgraderInventoryComponent,
    UpgraderTargetItemsComponent,
    UpgraderOffsetSliderComponent,
    UpgraderItemsInfoComponent,
    UpgraderTargetPanelComponent,
    BalanceIconComponent,
    InputOptionsComponent,
    FairnessButtonComponent,
    SvgIconComponent,
  ],
  templateUrl: './upgrader-new.component.html',
  styleUrl: './upgrader-new.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeInAnimation],
})
export class UpgraderNewComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  protected readonly upgraderService = inject(UpgraderNewService);
  protected readonly sessionService = inject(SessionService);
  protected readonly clipboardService = inject(ClipboardService);

  protected readonly showBalanceInput = signal(false);

  // Mobile tabs for bottom section
  protected readonly activeBottomTab = signal<'inventory' | 'targets'>('inventory');

  // Input options for 1/2, 2x, Max buttons
  protected readonly betInputOptions: { label: string; fn: () => void }[] = [
    { label: '1/2', fn: () => this.upgraderService.halveWager() },
    { label: '2x', fn: () => this.upgraderService.doubleWager() },
    { label: 'Max', fn: () => this.upgraderService.maxWager() },
  ];

  // Bet buttons for quick add values
  protected readonly betButtons: { label: string; fn: () => void }[] = [
    { label: '+1', fn: () => this.upgraderService.addToWager(1) },
    { label: '+10', fn: () => this.upgraderService.addToWager(10) },
    { label: '+25', fn: () => this.upgraderService.addToWager(25) },
    { label: '+100', fn: () => this.upgraderService.addToWager(100) },
  ];

  ngOnInit(): void {
    this.upgraderService.requestNewSeed();
  }

  ngOnDestroy(): void {
    this.upgraderService.resetAll();
  }

  protected toggleBalanceInput(): void {
    this.showBalanceInput.update(v => !v);
  }

  protected async onUpgrade(): Promise<void> {
    if (!this.upgraderService.isValid() || this.upgraderService.isProcessing()) {
      return;
    }

    await this.upgraderService.upgrade();
  }

  protected onAnimationComplete(result: { won: boolean; roll: number }): void {
    const isWon = result.won;

    // Update state to won/lost to show result colors immediately
    this.upgraderService.state.set(isWon ? 'won' : 'lost');
    this.upgraderService.finishAnimation();

    // Reset left/right panels immediately
    this.showBalanceInput.set(false);
    this.upgraderService.resetPanels(isWon);

    // Reset hexagon state after delay
    this.scheduleHexagonReset(isWon);
  }

  private scheduleHexagonReset(won: boolean): void {
    const targetState = won ? 'won' : 'lost';

    timer(HEXAGON_RESET_DELAY_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.upgraderService.state() === targetState) {
          this.upgraderService.completeRound();
        }
      });
  }

  protected onUserInteraction(): void {
    if (this.upgraderService.isResultState()) {
      this.upgraderService.completeRound();
    }
  }
}
